/**
 * Route Detail Page
 * Vue détaillée d'une route de production avec gestion des stops et actions
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { 
  ArrowLeft, Play, Square, Lock, Unlock, RefreshCw, 
  Plus, Clock, DollarSign, TrendingUp, Users, MapPin, X, Save 
} from 'lucide-react';
import '../../../styles/admin/production/RouteDetail.css';

const RouteDetail = () => {
  const { routeId } = useParams();
  const navigate = useNavigate();
  
  const [route, setRoute] = useState(null);
  const [stops, setStops] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [profitability, setProfitability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Modal state for adding stop
  const [showAddStopModal, setShowAddStopModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [stopFormData, setStopFormData] = useState({
    client_id: '',
    service_type: 'fertilisation',
    planned_revenue: 0,
    notes: ''
  });

  useEffect(() => {
    fetchRouteData();
    fetchClients();
  }, [routeId]);

  const fetchClients = async () => {
    try {
      // Fetch active clients for stop creation
      const response = await api.get('/api/clients?status=active');
      setClients(response.data || []);
    } catch (error) {
      console.error('Erreur chargement clients:', error);
    }
  };

  const fetchRouteData = async () => {
    try {
      setLoading(true);
      const [routeRes, stopsRes, employeesRes] = await Promise.all([
        api.get(`/api/production/routes/${routeId}`),
        api.get(`/api/production/routes/${routeId}/stops`),
        api.get(`/api/production/routes/${routeId}/employees`)
      ]);
      
      setRoute(routeRes.data);
      setStops(stopsRes.data);
      setEmployees(employeesRes.data);
      
      // Fetch profitability data
      try {
        const profRes = await api.get(`/api/production/routes/${routeId}/profitability`);
        setProfitability(profRes.data);
      } catch (profError) {
        console.log('Profitability data not available:', profError);
        setProfitability(null);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement route:', error);
      setLoading(false);
    }
  };

  const handleStartRoute = async () => {
    if (!window.confirm('Démarrer cette route ? Le chronomètre va commencer.')) return;
    
    try {
      setActionLoading(true);
      await api.post(`/api/production/routes/${routeId}/start`);
      await fetchRouteData();
      setActionLoading(false);
    } catch (error) {
      console.error('Erreur démarrage route:', error);
      alert(error.response?.data?.detail || 'Erreur lors du démarrage');
      setActionLoading(false);
    }
  };

  const handleEndRoute = async () => {
    if (!window.confirm('Terminer cette route ? Les calculs de rentabilité seront finalisés.')) return;
    
    try {
      setActionLoading(true);
      await api.post(`/api/production/routes/${routeId}/end`);
      await fetchRouteData();
      setActionLoading(false);
    } catch (error) {
      console.error('Erreur fin route:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la fin de route');
      setActionLoading(false);
    }
  };

  const handleRecalculate = async () => {
    try {
      setActionLoading(true);
      await api.post(`/api/production/routes/${routeId}/recalculate`);
      await fetchRouteData();
      setActionLoading(false);
    } catch (error) {
      console.error('Erreur recalcul:', error);
      setActionLoading(false);
    }
  };

  const handleLockToggle = async () => {
    const action = route.is_locked ? 'unlock' : 'lock';
    const message = route.is_locked 
      ? 'Déverrouiller cette route ? Les modifications seront à nouveau possibles.'
      : 'Verrouiller cette route ? Les modifications seront bloquées.';
    
    if (!window.confirm(message)) return;
    
    try {
      setActionLoading(true);
      await api.post(`/api/production/routes/${routeId}/${action}`);
      await fetchRouteData();
      setActionLoading(false);
    } catch (error) {
      console.error(`Erreur ${action}:`, error);
      alert(error.response?.data?.detail || `Erreur lors du ${action}`);
      setActionLoading(false);
    }
  };

  const handleAddStop = async (e) => {
    e.preventDefault();
    
    try {
      setActionLoading(true);
      await api.post(`/api/production/routes/${routeId}/stops`, stopFormData);
      await fetchRouteData();
      setShowAddStopModal(false);
      setStopFormData({
        client_id: '',
        service_type: 'fertilisation',
        planned_revenue: 0,
        notes: ''
      });
      setActionLoading(false);
    } catch (error) {
      console.error('Erreur ajout stop:', error);
      alert(error.response?.data?.detail || 'Erreur lors de l\'ajout du stop');
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      draft: { label: 'Brouillon', className: 'status-draft' },
      planned: { label: 'Planifiée', className: 'status-planned' },
      in_progress: { label: 'En cours', className: 'status-in-progress' },
      completed: { label: 'Terminée', className: 'status-completed' },
      cancelled: { label: 'Annulée', className: 'status-cancelled' }
    };
    const s = statusMap[status] || { label: status, className: '' };
    return <span className={`status-badge ${s.className}`}>{s.label}</span>;
  };

  const getStopStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'En attente', className: 'stop-pending' },
      in_progress: { label: 'En cours', className: 'stop-in-progress' },
      completed: { label: 'Terminé', className: 'stop-completed' },
      skipped: { label: 'Sauté', className: 'stop-skipped' },
      cancelled: { label: 'Annulé', className: 'stop-cancelled' }
    };
    const s = statusMap[status] || { label: status, className: '' };
    return <span className={`status-badge ${s.className}`}>{s.label}</span>;
  };

  if (loading) {
    return <div className="loading-container">Chargement...</div>;
  }

  if (!route) {
    return <div className="error-container">Route introuvable</div>;
  }

  const canStartRoute = ['draft', 'planned'].includes(route.status);
  const canEndRoute = route.status === 'in_progress';
  const canModify = !route.is_locked && route.status !== 'completed';

  return (
    <div className="route-detail-container">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate('/admin/production/routes')}>
            <ArrowLeft size={20} />
            Retour aux routes
          </button>
          <div className="route-title">
            <h1>
              {route.is_locked && <Lock size={24} className="lock-icon-title" />}
              {route.route_number}
            </h1>
            <p className="route-subtitle">{route.route_name}</p>
          </div>
        </div>
        <div className="header-actions">
          {canStartRoute && (
            <button 
              className="btn-success" 
              onClick={handleStartRoute}
              disabled={actionLoading}
            >
              <Play size={18} />
              Démarrer Route
            </button>
          )}
          {canEndRoute && (
            <button 
              className="btn-danger" 
              onClick={handleEndRoute}
              disabled={actionLoading}
            >
              <Square size={18} />
              Terminer Route
            </button>
          )}
          <button 
            className="btn-secondary" 
            onClick={handleRecalculate}
            disabled={actionLoading}
            title="Recalculer les indicateurs financiers"
          >
            <RefreshCw size={18} />
            Recalculer
          </button>
          <button 
            className="btn-secondary" 
            onClick={handleLockToggle}
            disabled={actionLoading}
          >
            {route.is_locked ? <Unlock size={18} /> : <Lock size={18} />}
            {route.is_locked ? 'Déverrouiller' : 'Verrouiller'}
          </button>
        </div>
      </div>

      {/* Infos Générales */}
      <div className="info-cards">
        <div className="info-card">
          <div className="info-label">Statut</div>
          <div className="info-value">{getStatusBadge(route.status)}</div>
        </div>
        <div className="info-card">
          <div className="info-label">Date</div>
          <div className="info-value">{new Date(route.route_date).toLocaleDateString('fr-CA')}</div>
        </div>
        <div className="info-card">
          <div className="info-label">Type de service</div>
          <div className="info-value">
            <span className="service-badge">{route.service_type}</span>
          </div>
        </div>
        <div className="info-card">
          <div className="info-label">Chef d'équipe</div>
          <div className="info-value">{route.crew_leader_id || '-'}</div>
        </div>
      </div>

      {/* KPIs Financiers & Rentabilité */}
      <div className="kpi-section">
        <h2>💰 Indicateurs de Rentabilité</h2>
        {profitability ? (
          <>
            <div className="kpi-cards">
              <div className="kpi-card revenue">
                <div className="kpi-icon"><DollarSign size={24} /></div>
                <div className="kpi-content">
                  <div className="kpi-label">Revenu Total</div>
                  <div className="kpi-value">${profitability.total_revenue.toFixed(2)}</div>
                  <div className="kpi-breakdown">
                    {profitability.completed_stops}/{profitability.total_stops} stops complétés
                  </div>
                </div>
              </div>
              
              <div className="kpi-card cost">
                <div className="kpi-icon"><TrendingUp size={24} /></div>
                <div className="kpi-content">
                  <div className="kpi-label">Coût Total</div>
                  <div className="kpi-value">${profitability.total_cost.toFixed(2)}</div>
                  <div className="kpi-breakdown">
                    MO: ${profitability.total_labor_cost.toFixed(2)} • 
                    Produits: ${profitability.total_product_cost.toFixed(2)}
                  </div>
                </div>
              </div>
              
              <div className={`kpi-card profit ${profitability.gross_profit >= 0 ? 'positive' : 'negative'}`}>
                <div className="kpi-icon"><TrendingUp size={24} /></div>
                <div className="kpi-content">
                  <div className="kpi-label">Profit Brut</div>
                  <div className={`kpi-value ${profitability.gross_profit >= 0 ? 'positive' : 'negative'}`}>
                    ${profitability.gross_profit.toFixed(2)}
                  </div>
                  <div className="kpi-breakdown">
                    Marge: {(profitability.margin_percent * 100).toFixed(1)}%
                  </div>
                </div>
              </div>
              
              <div className="kpi-card margin">
                <div className="kpi-icon"><TrendingUp size={24} /></div>
                <div className="kpi-content">
                  <div className="kpi-label">Marge Brute</div>
                  <div className={`kpi-value ${
                    profitability.margin_percent >= 0.4 ? 'excellent' : 
                    profitability.margin_percent >= 0.2 ? 'good' : 
                    profitability.margin_percent >= 0 ? 'low' : 'negative'
                  }`}>
                    {(profitability.margin_percent * 100).toFixed(1)}%
                  </div>
                  <div className="kpi-breakdown">
                    {profitability.margin_percent >= 0.4 ? '🟢 Excellent' : 
                     profitability.margin_percent >= 0.2 ? '🟡 Correct' : 
                     profitability.margin_percent >= 0 ? '🟠 Faible' : '🔴 Négatif'}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Métriques de Performance */}
            <div className="performance-metrics">
              <h3>📊 Performance & Écarts</h3>
              <div className="metrics-grid">
                <div className="metric-item">
                  <div className="metric-label">Durée prévue</div>
                  <div className="metric-value">{profitability.planned_duration_minutes} min</div>
                </div>
                <div className="metric-item">
                  <div className="metric-label">Durée réelle</div>
                  <div className="metric-value">{profitability.actual_duration_minutes} min</div>
                </div>
                <div className="metric-item">
                  <div className={`metric-label ${profitability.time_variance_minutes > 0 ? 'warning' : 'success'}`}>
                    Écart temps
                  </div>
                  <div className={`metric-value ${profitability.time_variance_minutes > 0 ? 'warning' : 'success'}`}>
                    {profitability.time_variance_minutes > 0 ? '+' : ''}{profitability.time_variance_minutes} min 
                    ({(profitability.time_variance_pct * 100).toFixed(1)}%)
                  </div>
                </div>
                <div className="metric-item">
                  <div className="metric-label">Employés sur route</div>
                  <div className="metric-value">{profitability.employee_ids.length}</div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="kpi-cards">
            <div className="kpi-card revenue">
              <div className="kpi-icon"><DollarSign size={24} /></div>
              <div className="kpi-content">
                <div className="kpi-label">Revenus</div>
                <div className="kpi-value">${route.total_service_revenue?.toFixed(2) || '0.00'}</div>
              </div>
            </div>
            
            <div className="kpi-card cost">
              <div className="kpi-icon"><TrendingUp size={24} /></div>
              <div className="kpi-content">
                <div className="kpi-label">Coûts Totaux</div>
                <div className="kpi-value">${route.total_cost?.toFixed(2) || '0.00'}</div>
                <div className="kpi-breakdown">
                  Main-d'œuvre: ${route.total_labor_cost?.toFixed(2) || '0.00'} • 
                  Produits: ${route.total_product_cost?.toFixed(2) || '0.00'}
                </div>
              </div>
            </div>
            
            <div className="kpi-card profit">
              <div className="kpi-icon"><TrendingUp size={24} /></div>
              <div className="kpi-content">
                <div className="kpi-label">Profit</div>
                <div className={`kpi-value ${(route.total_profit || 0) >= 0 ? 'positive' : 'negative'}`}>
                  ${route.total_profit?.toFixed(2) || '0.00'}
                </div>
              </div>
            </div>
            
            <div className="kpi-card">
              <div className="kpi-content">
                <div className="kpi-label">ℹ️ Données de rentabilité non calculées</div>
                <button className="btn-sm-primary" onClick={handleRecalculate}>
                  Calculer maintenant
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stats Opérationnelles */}
      <div className="stats-section">
        <div className="stat-item">
          <MapPin size={20} />
          <span><strong>{route.completed_stops}</strong> / {route.total_stops} stops complétés</span>
        </div>
        <div className="stat-item">
          <Clock size={20} />
          <span>
            <strong>{(route.total_service_minutes / 60).toFixed(1)}h</strong> service • 
            <strong> {(route.total_travel_minutes / 60).toFixed(1)}h</strong> déplacement
          </span>
        </div>
        <div className="stat-item">
          <Users size={20} />
          <span><strong>{employees.length}</strong> employés</span>
        </div>
      </div>

      {/* Liste des Stops */}
      <div className="stops-section">
        <div className="section-header">
          <h2>Arrêts Clients ({stops.length})</h2>
          {canModify && (
            <button className="btn-primary" onClick={() => setShowAddStopModal(true)}>
              <Plus size={18} />
              Ajouter un Stop
            </button>
          )}
        </div>

        <div className="stops-table-container">
          <table className="stops-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Client</th>
                <th>Adresse</th>
                <th>Service</th>
                <th>Statut</th>
                <th>Durée</th>
                <th>Revenus</th>
                <th>Coûts</th>
                <th>Profit</th>
                <th>Marge</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {stops.length === 0 ? (
                <tr>
                  <td colSpan="11" className="no-data">
                    Aucun arrêt client. Ajoutez votre premier stop pour commencer.
                  </td>
                </tr>
              ) : (
                stops.map((stop) => {
                  const revenue = stop.actual_revenue || stop.planned_revenue;
                  return (
                    <tr 
                      key={stop.id} 
                      onClick={() => navigate(`/admin/production/stops/${stop.id}`)}
                      className="clickable-row"
                    >
                      <td className="stop-number">{stop.stop_number}</td>
                      <td>{stop.client_name}</td>
                      <td className="address-cell">{stop.address}</td>
                      <td>
                        <span className="service-badge-small">{stop.service_type}</span>
                      </td>
                      <td>{getStopStatusBadge(stop.status)}</td>
                      <td>{stop.total_minutes > 0 ? `${stop.total_minutes} min` : '-'}</td>
                      <td className="currency">${revenue.toFixed(2)}</td>
                      <td className="currency">${stop.total_cost.toFixed(2)}</td>
                      <td className={`currency ${stop.gross_profit >= 0 ? 'positive' : 'negative'}`}>
                        ${stop.gross_profit.toFixed(2)}
                      </td>
                      <td className="margin">
                        <span className={stop.margin_percent >= 0 ? 'positive' : 'negative'}>
                          {stop.margin_percent.toFixed(1)}%
                        </span>
                      </td>
                      <td className="actions" onClick={(e) => e.stopPropagation()}>
                        <button 
                          className="btn-icon"
                          onClick={() => navigate(`/admin/production/stops/${stop.id}`)}
                          title="Voir détails"
                        >
                          <Play size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Notes */}
      {route.notes && (
        <div className="notes-section">
          <h3>Notes</h3>
          <p>{route.notes}</p>
        </div>
      )}

      {/* Modal Ajout Stop */}
      {showAddStopModal && (
        <div className="modal-overlay" onClick={() => setShowAddStopModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Ajouter un Arrêt Client</h3>
              <button className="modal-close" onClick={() => setShowAddStopModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleAddStop} className="modal-form">
              <div className="form-group">
                <label>Client *</label>
                <select
                  required
                  value={stopFormData.client_id}
                  onChange={(e) => setStopFormData({...stopFormData, client_id: e.target.value})}
                >
                  <option value="">Sélectionner un client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.first_name} {client.last_name} - {client.address}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Type de Service *</label>
                <select
                  required
                  value={stopFormData.service_type}
                  onChange={(e) => setStopFormData({...stopFormData, service_type: e.target.value})}
                >
                  <option value="fertilisation">Fertilisation</option>
                  <option value="tonte">Tonte</option>
                  <option value="deneigement">Déneigement</option>
                  <option value="aerification">Aérification</option>
                </select>
              </div>

              <div className="form-group">
                <label>Revenu Prévu ($) *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={stopFormData.planned_revenue}
                  onChange={(e) => setStopFormData({...stopFormData, planned_revenue: parseFloat(e.target.value)})}
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  rows={3}
                  value={stopFormData.notes}
                  onChange={(e) => setStopFormData({...stopFormData, notes: e.target.value})}
                  placeholder="Notes sur le service..."
                />
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowAddStopModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary" disabled={actionLoading}>
                  <Save size={16} />
                  {actionLoading ? 'Ajout...' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteDetail;
